import { useQuery } from "@tanstack/react-query";
import { CalendarCheck, MessageCircle, ShieldCheck, Star } from "lucide-react";
import { useActiveLang } from "@/hooks/use-active-lang";
import { SITE } from "@/lib/site";

const T = {
  es: { h: "Por qué reservar con nosotros", items: [["Pago seguro con Stripe", "Pagas con tarjeta en la pasarela cifrada de Stripe."], ["Disponibilidad real", "Fechas y lugares salen en vivo del sistema de reservas del operador."], ["Atención 24/7 por WhatsApp", "Te ayudamos antes, durante y después de tu tour."]], rating: (a: string, n: number) => [`${a} de 5`, `Promedio de ${n} reseñas verificadas de viajeros que reservaron.`] },
  en: { h: "Why book with us", items: [["Secure payment with Stripe", "Pay by card through Stripe's encrypted checkout."], ["Real availability", "Dates and spots come live from the operator's booking system."], ["24/7 support on WhatsApp", "We help you before, during and after your tour."]], rating: (a: string, n: number) => [`${a} out of 5`, `Average of ${n} verified reviews from travelers who booked.`] },
  fr: { h: "Pourquoi réserver avec nous", items: [["Paiement sécurisé avec Stripe", "Paiement par carte via le système chiffré de Stripe."], ["Disponibilité réelle", "Dates et places en direct depuis le système de l'opérateur."], ["Assistance 24h/24 et 7j/7 sur WhatsApp", "Nous vous aidons avant, pendant et après l'excursion."]], rating: (a: string, n: number) => [`${a} sur 5`, `Moyenne de ${n} avis vérifiés de voyageurs ayant réservé.`] },
  it: { h: "Perché prenotare con noi", items: [["Pagamento sicuro con Stripe", "Paghi con carta tramite il checkout cifrato di Stripe."], ["Disponibilità reale", "Date e posti in tempo reale dal sistema dell'operatore."], ["Assistenza 24/7 su WhatsApp", "Ti aiutiamo prima, durante e dopo il tour."]], rating: (a: string, n: number) => [`${a} su 5`, `Media di ${n} recensioni verificate di chi ha prenotato.`] },
  de: { h: "Warum bei uns buchen", items: [["Sichere Zahlung mit Stripe", "Kartenzahlung über den verschlüsselten Stripe-Checkout."], ["Echte Verfügbarkeit", "Termine und Plätze live aus dem Buchungssystem des Anbieters."], ["24/7-Hilfe per WhatsApp", "Wir helfen vor, während und nach deiner Tour."]], rating: (a: string, n: number) => [`${a} von 5`, `Durchschnitt aus ${n} verifizierten Bewertungen von Gästen mit Buchung.`] },
  pt: { h: "Por que reservar conosco", items: [["Pagamento seguro com Stripe", "Pague com cartão pelo checkout criptografado da Stripe."], ["Disponibilidade real", "Datas e vagas ao vivo do sistema de reservas do operador."], ["Atendimento 24/7 pelo WhatsApp", "Ajudamos antes, durante e depois do passeio."]], rating: (a: string, n: number) => [`${a} de 5`, `Média de ${n} avaliações verificadas de quem reservou.`] },
};
const ICONS = [ShieldCheck, CalendarCheck, MessageCircle];

/** Calificación solo con reseñas aprobadas reales; si no hay, no se muestra. */
function useRating() {
  return useQuery({
    queryKey: ["public-rating-summary"],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error } = await supabase.from("tour_reviews").select("rating").eq("status", "approved").eq("is_seed", false).limit(1000);
      if (error || !data?.length) return null;
      const r = data.map((d) => Number(d.rating)).filter((n) => n >= 1 && n <= 5);
      if (!r.length) return null;
      return { avg: r.reduce((a, b) => a + b, 0) / r.length, n: r.length };
    },
  });
}

export function WhyBookWithUs() {
  const lang = useActiveLang();
  const L = T[lang as keyof typeof T] ?? T.es;
  const { data: rating } = useRating();
  const items: [string, string][] = [...L.items] as [string, string][];
  if (rating) items.push(L.rating(rating.avg.toFixed(1), rating.n) as [string, string]);
  return (
    <section aria-labelledby="why-book" className="border-y border-border/60 bg-secondary">
      <div className="mx-auto max-w-[1360px] px-4 py-10 sm:px-6 lg:px-8">
        <h2 id="why-book" className="font-display text-2xl font-semibold text-foreground sm:text-3xl">{L.h}</h2>
        <ul className={`mt-6 grid gap-4 sm:grid-cols-2 ${items.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
          {items.map(([title, desc], i) => {
            const Icon = ICONS[i] ?? Star;
            const body = (
              <>
                <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${i === 3 ? "bg-gold/15 text-gold-dark" : "bg-primary/10 text-primary"}`}>
                  <Icon className="h-5 w-5" aria-hidden />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-foreground">{title}</span>
                  <span className="mt-0.5 block text-[13px] leading-snug text-muted-foreground">{desc}</span>
                </span>
              </>
            );
            return (
              <li key={title} className="flex h-full gap-3 rounded-2xl bg-background p-4 ring-1 ring-border/60">
                {i === 2 ? (
                  <a href={SITE.contact.whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex gap-3">{body}</a>
                ) : body}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
